// Minimal document-head manager for per-route social metadata.
// Values are always plain text (see lib/share.ts) and are written with
// setAttribute, so no markup can be injected.

type MetaSpec = {
  title: string;
  description: string;
  url: string;
  image?: string;
  imageAlt?: string;
  card?: string;
  type?: "profile" | "website";
  robots?: string;
};

const MANAGED = "data-siis-head";

function upsert(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute(MANAGED, "1");
    document.head.appendChild(el);
  }
  apply(el);
}

function meta(attr: "name" | "property", key: string, value?: string) {
  if (!value) return;
  upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  }, (el) => el.setAttribute("content", value));
}

/** Apply per-page head metadata; returns a cleanup that removes tags we added. */
export function applyHeadMeta(spec: MetaSpec): () => void {
  const prevTitle = document.title;
  const previous = Array.from(document.head.querySelectorAll<HTMLElement>("meta[name], meta[property], link[rel=canonical]"))
    .map((el) => ({ el, content: el.getAttribute("content"), href: el.getAttribute("href") }));
  const addedBefore = new Set(document.head.querySelectorAll(`[${MANAGED}]`));
  document.title = spec.title;

  meta("name", "description", spec.description);
  meta("property", "og:type", spec.type ?? "profile");
  meta("name", "robots", spec.robots);
  meta("property", "og:title", spec.title);
  meta("property", "og:description", spec.description);
  meta("property", "og:url", spec.url);
  meta("property", "og:image", spec.image);
  meta("property", "og:image:alt", spec.imageAlt);
  meta("name", "twitter:card", spec.card ?? "summary_large_image");
  meta("name", "twitter:title", spec.title);
  meta("name", "twitter:description", spec.description);
  meta("name", "twitter:image", spec.image);

  upsert('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  }, (el) => el.setAttribute("href", spec.url));

  return () => {
    document.title = prevTitle;
    document.head.querySelectorAll(`[${MANAGED}]`).forEach((el) => { if (!addedBefore.has(el)) el.remove(); });
    previous.forEach(({ el, content, href }) => {
      if (content !== null) el.setAttribute("content", content);
      if (href !== null) el.setAttribute("href", href);
    });
  };
}
